export const DigitalAuditTab = {
  SCAN: 'scan',
  REPORT: 'report',
  SITE_HEALTH: 'site-health',
  PIXEL_INTEL: 'pixel-intel',
  CHANNEL_HEALTH: 'channel-health',
  JOURNEY: 'journey',
  CONFIGURATION: 'configuration',
  CLIENT_GUIDE: 'client-guide',
  GLOSSARY: 'glossary'
} as const;
export type DigitalAuditTabType = (typeof DigitalAuditTab)[keyof typeof DigitalAuditTab];

export const DIGITAL_AUDIT_NAVS = [
  { key: DigitalAuditTab.SCAN, label: 'Scan' },
  { key: DigitalAuditTab.REPORT, label: 'Audit Report' },
  { key: DigitalAuditTab.SITE_HEALTH, label: 'Site Health' },
  { key: DigitalAuditTab.PIXEL_INTEL, label: 'Pixel Intel' },
  { key: DigitalAuditTab.CHANNEL_HEALTH, label: 'Channel Health' },
  { key: DigitalAuditTab.JOURNEY, label: 'Journey' },
  { key: DigitalAuditTab.CONFIGURATION, label: 'Configuration' },
  { key: DigitalAuditTab.CLIENT_GUIDE, label: 'Client Guide' },
  { key: DigitalAuditTab.GLOSSARY, label: 'Glossary' }
];

// A full scan's results replace the quick scan's in the same report/tabs
// (see engine/full-scan-merge-utils.ts) - this tracks which one produced
// the report's current scanResult, for the top-of-page status banner.
export const ScanSource = {
  QUICK: 'quick',
  FULL: 'full'
} as const;
export type ScanSourceType = (typeof ScanSource)[keyof typeof ScanSource];

// Values must match the real CLIENT_TYPES keys in engine/journey-data.ts
// (generated from the source tool's Google Sheet) — these are not
// arbitrary, they're foreign keys into that data.
export const ClientType = {
  ECOMM: 'ecomm',
  LEAD_GEN: 'leadgen',
  ECOMM_SUBSCRIPTION: 'ecomm_sub',
  ECOMM_IN_STORE: 'ecomm_store',
  HYBRID: 'hybrid'
} as const;
export type ClientTypeType = (typeof ClientType)[keyof typeof ClientType];

export const isClientType = (value: string): value is ClientTypeType =>
  (Object.values(ClientType) as string[]).includes(value);

export interface ScanInput {
  url: string;
  clientName: string;
  clientType: ClientTypeType;
  vertical: string;
  // CSS selector for the site's cookie-consent "Accept" button. Only used by
  // the full browser crawl (see full-scan/full-scan-api.ts's startFullScan)
  // to click through consent and capture what additionally fires once it's
  // granted - the quick static scan never executes JS, so it ignores this.
  consentSelector?: string;
}

// The actual detection/scoring/journey shapes (Detections, ScanResult,
// JourneyModel, etc.) live in engine/*-constants.ts, ported faithfully from
// the source Python tool's data model. AuditReport wraps them with the
// input that produced them, for persistence and display.
export interface AuditReport {
  scanId: string;
  input: ScanInput;
  scanResult: import('./engine/martech-scanner-constants').ScanResult;
  journey: import('./engine/journey-mapper-constants').JourneyModel;
  // Which scan produced the current scanResult/journey - starts 'quick', and
  // flips to 'full' once a completed full-scan job's results are merged in.
  scanSource: ScanSourceType;
  // Set as soon as "also run a full site scan" kicks off a job for this
  // report, null otherwise. Drives both the polling hook and the banner.
  fullScanJobId: string | null;
  // Snapshot of the quick scan's Load Speed/Discoverability facts, captured
  // once here and never touched by a full-scan merge (see
  // full-scan-merge-utils.ts, which clears these same facts off scanResult
  // since they were measured against a different page set). SiteHealthView
  // reads them from here unconditionally so a full scan only ever upgrades
  // JavaScript rendering (recomputed from real evidence) instead of blanking
  // the rest of the tab.
  quickScanSiteHealth: {
    pageMetrics?: import('./engine/martech-scanner-constants').PageMetric[];
    discoverability?: import('./engine/martech-scanner-constants').DiscoverabilityInfo;
  };
  // Set when a full-scan job merges in, whether it finished DONE or only
  // PARTIAL (deadline/page-cap reached before every targeted page - see
  // full-scan/full-scan-constants.ts). A partial crawl still carries real
  // per-page evidence for whatever it did capture, so the merge no longer
  // waits for DONE; this says how much of the crawl the merged data reflects.
  fullScanPageCoverage: { pagesCaptured: number; pagesTotal: number } | null;
  // DEV-24211: the last milestone list the user applied on the Journey tab
  // (null until they've made an edit). journey.tab already reflects it, but
  // that's a rebuilt JourneyModel, not the edit list itself - a background
  // full-scan merge needs the raw list to pass back into buildJourney, or it
  // silently reverts to the template on merge.
  editedMilestones: import('./engine/journey-mapper-constants').EditableMilestone[] | null;
}
